import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiRequest } from "@/lib/queryClient";
import {
  Appearance,
  isAppearance,
  readCachedAppearance,
  resolveTheme,
  writeCachedAppearance,
} from "@/lib/theme";

interface ThemeContextType {
  // The saved setting (what the user chose), not the resolved light/dark.
  appearance: Appearance;
  // Applies and caches immediately, then saves. If the save fails the previous
  // value is restored and the returned promise rejects so the caller can say so.
  // Signed out, it only changes the local value (nothing to save to).
  setAppearance: (appearance: Appearance) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const DARK_QUERY = "(prefers-color-scheme: dark)";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  // Seeded from the cache the pre-paint script in index.html also read, so the
  // first render agrees with what is already on screen.
  const [appearance, setLocalAppearance] = useState<Appearance>(() => readCachedAppearance() ?? "system");
  const userId = user?.id;
  // Bumped on every user choice so a slow initial GET, or an older failed
  // save, can tell it has been superseded and must not overwrite the choice.
  const choiceSeq = useRef(0);
  const current = useRef(appearance);
  current.current = appearance;

  // Signed out: follow the system and forget the previous user's setting.
  // Signed in: read the saved preference. Any failure leaves the current value.
  useEffect(() => {
    if (isLoading) return;

    if (userId === undefined) {
      writeCachedAppearance(undefined);
      setLocalAppearance("system");
      return;
    }

    let cancelled = false;
    const startedAt = choiceSeq.current;
    apiRequest("GET", "/v1/users/me/preferences")
      .then((res) => res.json())
      .then((prefs) => {
        if (cancelled || choiceSeq.current !== startedAt) return;
        const next = isAppearance(prefs?.appearance) ? prefs.appearance : "system";
        writeCachedAppearance(next);
        setLocalAppearance(next);
      })
      .catch(() => {
        // Keep the cached/current value; an outage should not flip the theme.
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isLoading]);

  // Apply to <html>; under "system", track the OS setting live.
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const apply = () => {
      const theme = resolveTheme(appearance, media.matches);
      document.documentElement.classList.toggle("dark", theme === "dark");
    };
    apply();
    if (appearance !== "system") return;
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [appearance]);

  const setAppearance = useCallback(
    async (next: Appearance) => {
      const previous = current.current;
      const seq = ++choiceSeq.current;
      current.current = next;
      setLocalAppearance(next);
      if (userId === undefined) return;

      writeCachedAppearance(next);
      try {
        await apiRequest("PATCH", "/v1/users/me/preferences", { appearance: next });
      } catch (error) {
        // Only undo if nothing newer was chosen while this save was in flight.
        if (choiceSeq.current === seq) {
          current.current = previous;
          setLocalAppearance(previous);
          writeCachedAppearance(previous);
        }
        throw error;
      }
    },
    [userId],
  );

  return <ThemeContext.Provider value={{ appearance, setAppearance }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
